'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import { AK } from '@/constants';
import { checkerArray } from '@/utilities/checker';
import SvgIcons from '../themes/SvgIcons';
import { getAssetsBaseUrl } from '@/services/storeService';
import { motion, AnimatePresence } from "framer-motion"; // Added framer-motion

import Ipl from '@/public/assets/images/ipl.png';
import gbu from '@/public/assets/images/gbu.png';
import Iplmobile from '@/public/assets/images/iplmobile.png';
import gbumobile from '@/public/assets/images/gbumobile.png';
import {
  Autoplay,
  Navigation,
  Scrollbar,
  A11y,
  Pagination,
} from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const Homepage = ({
  categoryListData,
  featuredcategoriesListData,
  bestsellersListData,
  topratedListData,
}: {
  categoryListData: any;
  featuredcategoriesListData: any;
  bestsellersListData: any;
  topratedListData: any;
}) => {
  const router = useRouter();
  const assets = getAssetsBaseUrl();
  const [hoveredProductId, setHoveredProductId] = useState<number | null>(null);

  const navigateToProduct = (seoUrl: string) => {
    router.push(`/p/${seoUrl}`);
  };

  const ProductCard = ({ product }: { product: any }) => {
    const images = product?.imageurl?.split(',') || [];
    const defaultImage = checkerArray(images, 1) ? images[0] : '';
    const hoverImage = checkerArray(images, 2) ? images[1] : defaultImage;

    return (
      <div
        className="group relative cursor-pointer p-2 w-full sm:w-1/2 md:w-1/3 lg:w-1/4"
        onClick={() => navigateToProduct(product.seo_url)}
      >
        <div className="overflow-hidden rounded-xl bg-gray-50">
          <div
            onMouseEnter={() => setHoveredProductId(product.id)}
            onMouseLeave={() => setHoveredProductId(null)}
            className="aspect-[4/5] relative"
          >
            <Image
              src={
                hoveredProductId === product.id && hoverImage
                  ? assets + hoverImage
                  : assets + defaultImage
              }
              alt={product.name}
              fill
              sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
              className="object-cover rounded-xl transition-transform duration-500 group-hover:scale-105"
              onError={(e) => {
                e.currentTarget.src = '/ecomsaas-assets/images/no-image.webp';
              }}
            />
          </div>
        </div>

        <p className="mt-3 text-sm md:text-base font-medium text-gray-900 line-clamp-2">
          {product.name.length > 30 ? `${product.name.substring(0, 30)}...` : product.name}
        </p>

        <div className="mt-1 flex items-center gap-2 text-lg font-medium">
          <span className="text-black">₹{product.sp}</span>
          {product.mrp > product.sp && (
            <del className="text-gray-500 text-sm">₹{product.mrp}</del>
          )}
        </div>
      </div>
    );
  };

  return (
    <>


      {/* Hero Banner Slider */}
      <div className="overflow-hidden">
        {/* Desktop */}
        <div className="hidden sm:flex animate-[slide_12s_infinite_linear]">
          <Link href="/products/ipl-launch" className="flex-shrink-0 w-full">
            <Image src={Ipl} alt="IPL Collection" className="w-full" />
          </Link>
          <Link href="/products/Good-bad-ugly" className="flex-shrink-0 w-full">
            <Image src={gbu} alt="Good Bad Ugly" className="w-full" />
          </Link>
        </div>

        {/* Mobile */}
        <div className="sm:hidden flex animate-[slide_12s_infinite_linear]">
          <Link href="/products/ipl-launch" className="flex-shrink-0 w-full">
            <Image src={Iplmobile} alt="IPL Collection" className="w-full" />
          </Link>
          <Link href="/products/Good-bad-ugly" className="flex-shrink-0 w-full">
            <Image src={gbumobile} alt="Good Bad Ugly" className="w-full" />
          </Link>
        </div>
      </div>

      {/* New Products Section */}
      <section className="container mx-auto px-4 py-8">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 tracking-wider">
          NEW PRODUCTS
        </h2>
        <div className="flex flex-wrap -mx-2">
          {featuredcategoriesListData[1]?.products?.map((product: any) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
        <div className="text-center mt-10">
          <Link
            href="/products/polo-tshirt"
            className="inline-block px-12 py-4 border-2 border-black text-black font-medium hover:bg-black hover:text-white transition"
          >
            DISCOVER MORE
          </Link>
        </div>
      </section>

      {/* Round Neck T-Shirts */}
      <section className="container mx-auto px-4 py-8">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 tracking-wider">
          ROUND NECK TSHIRT
        </h2>
        <div className="flex flex-wrap -mx-2">
          {featuredcategoriesListData[0]?.products?.map((product: any) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
        <div className="text-center mt-10">
          <Link
            href="/products/round-neck-tshirt"
            className="inline-block px-12 py-4 border-2 border-black text-black font-medium hover:bg-black hover:text-white transition"
          >
            DISCOVER MORE
          </Link>
        </div>
      </section>

      {/* Quality Badges */}
      <section className="bg-gray-100 py-12 hidden md:block">
        <div className="container mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { Icon: SvgIcons.Quality, title: "100% Quality Products" },
            { Icon: SvgIcons.Truck, title: "World Wide Delivery Available" },
            { Icon: SvgIcons.Payment, title: "100% Secure Payments" },
            { Icon: SvgIcons.Return, title: "Easy Returns & Instant Refunds" },
          ].map(({ Icon, title }) => (
            <div key={title} className="flex flex-col items-center">
              <Icon className="w-16 h-16 mb-4" />
              <p className="text-sm font-medium">{title}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Polo T-Shirts */}
      <section className="container mx-auto px-4 py-8">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 tracking-wider">
          POLO TSHIRT
        </h2>
        <div className="flex flex-wrap -mx-2">
          {featuredcategoriesListData[1]?.products?.map((product: any) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
        <div className="text-center mt-10">
          <Link
            href="/products/polo-tshirt"
            className="inline-block px-12 py-4 border-2 border-black text-black font-medium hover:bg-black hover:text-white transition"
          >
            DISCOVER MORE
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold mb-6 tracking-wider">
            DISCOVER ABOUT FAN BOY STORE
          </h2>
          <p className="text-gray-700 leading-relaxed text-sm md:text-base">
            fanboystore is a lifestyle fashion brand committed to crafting innovative and distinctive
            fashion for the trendy, contemporary Indian. Our brand was born out of a dedication to
            making a positive impact through innovation, honesty, and thoughtfulness...
          </p>
        </div>
      </section>

      <hr className="border-t border-gray-300 container mx-auto" />

      {/* Add this to your global CSS (app/globals.css or tailwind.css) */}
      <style jsx global>{`
        @keyframes slide {
          0% { transform: translateX(0); }
          50% { transform: translateX(-100%); }
          50.01% { transform: translateX(100%); }
          100% { transform: translateX(0); }
        }
        .animate-[slide_12s_infinite_linear] {
          animation: slide 12s infinite linear;
        }
      `}</style>
    </>
  );
};

export default Homepage;